import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Grid,
  Paper,
  Box,
  Typography,
  TextField,
  InputAdornment,
  Tooltip,
  Button,
  Stack,
} from "@mui/material";
import {
  Search as SearchIcon,
  Warning,
  Update,
} from "@mui/icons-material";
import { GridColDef, GridValueGetterParams } from "@mui/x-data-grid";
import NavigationHeader from "components/NavigationHeader";
import { StripedDataGrid } from "components/StripedDataGrid";
import { DashboardStackCustom } from "components/StackCustom/styles";
import { useNavigation } from "contexts/notificationsContext";
import {
  getTipologiaPorId,
  getTipologiaPerfis,
  getTipologiaComponentes,
  getTipologiaImagens,
} from "services/modules/tipologias";
import {
  ITipologiaDetalhe,
  ITipologiaPerfil,
  ITipologiaComponente,
  ITipologiaImagem,
} from "shared/interfaces/tipologia";

type TipologiaPageMode = "solicitacao" | "consulta";

interface TipologiaDetalheProps {
  mode?: TipologiaPageMode;
}

// URL base para assets estáticos (imagens) quando o backend retorna caminhos relativos.
// Quando a URL já for absoluta (http/https/data), ela é usada como está.
const API_ASSETS_BASE_URL =
  (window as any).configs?.REACT_APP_BASE_URL?.replace("/api/", "") || "http://localhost:3099";

const buildImageSrc = (url?: string | null): string => {
  if (!url) return "";
  const trimmed = url.trim();
  if (trimmed.startsWith("data:")) return trimmed;
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;

  // Se já contém query string (ex: /api/Pacote/imagem?path=...), usar como está
  if (trimmed.includes("?path=") || trimmed.includes("/api/Pacote/imagem")) {
    if (trimmed.startsWith("/")) {
      return `${API_ASSETS_BASE_URL}${trimmed}`;
    }
    return `${API_ASSETS_BASE_URL}/${trimmed}`;
  }

  // Se é apenas um nome de arquivo (sem caminho, sem "/"), provavelmente é uma imagem de componente/perfil
  // Montar URL usando o endpoint /api/Pacote/imagem
  if (!trimmed.includes("/") && !trimmed.startsWith("/")) {
    const fileName = trimmed.toLowerCase();
    
    // Detectar tipo de imagem pelo nome do arquivo
    // Componentes geralmente começam com "P-", "fc_", "RCP-", etc.
    const isComponente = fileName.startsWith("p-") || 
                         fileName.startsWith("fc_") || 
                         fileName.startsWith("rcp-") ||
                         fileName.startsWith("p-con-") ||
                         fileName.startsWith("p-fec-") ||
                         fileName.startsWith("p-fit-") ||
                         fileName.startsWith("p-bot-") ||
                         fileName.startsWith("p-capa-") ||
                         fileName.startsWith("p-cfec-") ||
                         fileName.startsWith("p-gua-");
    
    // Perfis geralmente começam com "fp_" ou são códigos de perfil (ex: CHR058, CHR-IPC200)
    // Se não for claramente um componente, assume que é perfil
    const isPerfil = fileName.startsWith("fp_") || !isComponente;
    
    let path: string;
    if (isComponente) {
      path = `assets/figuras_componente/${trimmed}`;
    } else {
      // Assume perfil se não for componente
      path = `assets/figuras_perfil/${trimmed}`;
    }
    
    const encodedPath = encodeURIComponent(path);
    return `${API_ASSETS_BASE_URL}/api/Pacote/imagem?path=${encodedPath}`;
  }

  // Caminhos relativos (ex.: "/assets/..." ou "assets/...") continuam indo via API_ASSETS_BASE_URL
  if (trimmed.startsWith("/")) {
    return `${API_ASSETS_BASE_URL}${trimmed}`;
  }
  return `${API_ASSETS_BASE_URL}/${trimmed}`;
};

const decodeLegacyText = (value?: string | null): string => {
  if (!value) return "";

  // Se já contém caracteres Unicode válidos (acima de 255), retorna como está
  const hasHighCodePoint = Array.from(value).some(
    (char) => char.charCodeAt(0) > 255
  );
  if (hasHighCodePoint) {
    return value;
  }

  try {
    // Tenta decodificar como URL encoding (método original)
    const encoded = Array.from(value)
      .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`)
      .join("");
    const decoded = decodeURIComponent(encoded);
    
    // Se a decodificação funcionou e resultou em algo diferente, retorna
    if (decoded !== value) {
      return decoded;
    }
    
    // Se não mudou, tenta tratar como Windows-1252/ISO-8859-1 para acentos
    // Mapeamento de caracteres comuns do Windows-1252 para UTF-8
    const windows1252Map: Record<number, string> = {
      // Acentos maiúsculos
      0xC0: "À", 0xC1: "Á", 0xC2: "Â", 0xC3: "Ã", 0xC4: "Ä", 0xC5: "Å",
      0xC7: "Ç",
      0xC8: "È", 0xC9: "É", 0xCA: "Ê", 0xCB: "Ë",
      0xCC: "Ì", 0xCD: "Í", 0xCE: "Î", 0xCF: "Ï",
      0xD1: "Ñ",
      0xD2: "Ò", 0xD3: "Ó", 0xD4: "Ô", 0xD5: "Õ", 0xD6: "Ö",
      0xD9: "Ù", 0xDA: "Ú", 0xDB: "Û", 0xDC: "Ü",
      0xDD: "Ý",
      // Acentos minúsculos
      0xE0: "à", 0xE1: "á", 0xE2: "â", 0xE3: "ã", 0xE4: "ä", 0xE5: "å",
      0xE7: "ç",
      0xE8: "è", 0xE9: "é", 0xEA: "ê", 0xEB: "ë",
      0xEC: "ì", 0xED: "í", 0xEE: "î", 0xEF: "ï",
      0xF1: "ñ",
      0xF2: "ò", 0xF3: "ó", 0xF4: "ô", 0xF5: "õ", 0xF6: "ö",
      0xF9: "ù", 0xFA: "ú", 0xFB: "û", 0xFC: "ü",
      0xFD: "ý", 0xFF: "ÿ",
    };
    
    let result = "";
    for (let i = 0; i < value.length; i++) {
      const code = value.charCodeAt(i);
      if (windows1252Map[code]) {
        result += windows1252Map[code];
      } else if (code < 128) {
        // ASCII válido, mantém como está
        result += value[i];
      } else {
        // Caractere não mapeado, mantém original
        result += value[i];
      }
    }
    
    return result !== value ? result : value;
  } catch {
    return value;
  }
};

const formatDisplayText = (value?: string | null): string => {
  const decoded = decodeLegacyText(value);
  return decoded && decoded.trim() !== "" ? decoded : "-";
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Componente para renderizar imagem com fallback (usado nas tabelas)
const ImageCell: React.FC<{ src: string | null; alt: string; maxWidth?: string; maxHeight?: string }> = ({ 
  src, 
  alt, 
  maxWidth = "80px", 
  maxHeight = "40px" 
}) => {
  const [imageError, setImageError] = useState(false);
  const imageSrc = src ? buildImageSrc(src) : null;
  
  if (!imageSrc) {
    return (
      <div style={{ padding: "4px" }}>
        <div style={{ fontSize: "10px", color: "#999" }}>-</div>
      </div>
    );
  }
  
  if (imageError) {
    return (
      <div style={{ padding: "4px" }}>
        <Box
          sx={{
            width: maxWidth,
            height: maxHeight,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "1px dashed #ccc",
            borderRadius: 1,
            fontSize: "10px",
            color: "#999",
            backgroundColor: "#f5f5f5",
          }}
        >
          Sem img
        </Box>
      </div>
    );
  }
  
  return (
    <Tooltip
      title={
        <Box
          component="img"
          src={imageSrc}
          alt={alt}
          sx={{
            width: "230px",
            maxHeight: "230px",
            objectFit: "contain",
            display: "block",
            backgroundColor: "#fff",
            borderRadius: 1,
          }}
        />
      }
      arrow
      placement="right"
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: "transparent",
            padding: 0,
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            maxWidth: "none",
          },
        },
        arrow: {
          sx: {
            color: "#fff",
          },
        },
      }}
    >
      <div style={{ padding: "4px", cursor: "pointer" }}>
        <img
          src={imageSrc}
          alt={alt}
          onError={() => setImageError(true)}
          style={{ maxWidth, maxHeight, objectFit: "contain" }}
        />
      </div>
    </Tooltip>
  );
};

// Componente para renderizar croqui com fallback
const CroquiImageWithFallback: React.FC<{ src: string; alt: string }> = ({ src, alt }) => {
  const [imageError, setImageError] = useState(false);
  
  if (imageError) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "200px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "1px dashed #ccc",
          borderRadius: 1,
          color: "#999",
          backgroundColor: "#f5f5f5",
        }}
      >
        Sem imagem
      </Box>
    );
  }
  
  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      onError={() => setImageError(true)}
      sx={{
        width: "100%",
        maxHeight: "200px",
        objectFit: "contain",
        border: "1px solid #eee",
        borderRadius: 1,
        p: 1,
      }}
    />
  );
};

const TipologiaDetalheInner = ({ mode = "solicitacao" }: TipologiaDetalheProps) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { openNotification } = useNavigation();

  const [tipologia, setTipologia] = useState<ITipologiaDetalhe | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [searchLocal, setSearchLocal] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [perfis, setPerfis] = useState<ITipologiaPerfil[]>([]);
  const [componentes, setComponentes] = useState<ITipologiaComponente[]>([]);
  const [imagens, setImagens] = useState<ITipologiaImagem[]>([]);

  useEffect(() => {
    if (id) {
      loadTipologia(parseInt(id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (tipologia) {
      loadTabData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, tipologia]);

  // Carregar imagens imediatamente quando a tipologia for carregada (para exibir o croqui)
  useEffect(() => {
    if (tipologia) {
      loadImagens();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipologia]);

  const loadImagens = async () => {
    if (!tipologia) return;
    try {
      const imagensData = await getTipologiaImagens(tipologia.idTipologia);
      setImagens(imagensData || []);
    } catch (error) {
      console.error("Erro ao carregar imagens da tipologia:", error);
      setImagens([]);
    }
  };

  const loadTipologia = async (idTipologia: number) => {
    setIsLoading(true);
    try {
      const data = await getTipologiaPorId(idTipologia);
      setTipologia(data);
    } catch (error) {
      openNotification({
        message: "Erro ao carregar tipologia",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadTabData = async () => {
    if (!tipologia) return;

    setIsLoading(true);
    try {
      switch (tabValue) {
        case 0: // Perfil
          const perfisData = await getTipologiaPerfis(tipologia.idTipologia, searchLocal);
          setPerfis(perfisData);
          break;
        case 1: // Componentes
          const componentesData = await getTipologiaComponentes(tipologia.idTipologia, searchLocal);
          setComponentes(componentesData);
          break;
      }
    } catch (error) {
      openNotification({
        message: "Erro ao carregar dados da aba",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setSearchLocal("");
  };

  const handleButtonTabChange = (newValue: number) => {
    setTabValue(newValue);
    setSearchLocal("");
  };

  const handleSearchLocal = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchLocal(value);
    // Recarrega dados com filtro
    setTimeout(() => loadTabData(), 300);
  };

  const columnsPerfis: GridColDef[] = [
    {
      field: "ordem",
      headerName: "#",
      width: 60,
    },
    {
      field: "urlImagem",
      headerName: "Imagem",
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <ImageCell 
          src={params.value} 
          alt={params.row.codigoPerfil || "Imagem do perfil"} 
        />
      ),
    },
    {
      field: "codigoPerfil",
      headerName: "Código",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {params.value}
          {params.row.descontinuado && (
            <Tooltip title={params.row.motivoDescontinuacao || "Perfil descontinuado"}>
              <Warning color="error" fontSize="small" />
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      field: "descricaoPerfil",
      headerName: "Descrição",
      flex: 2,
      minWidth: 250,
      valueGetter: (params: GridValueGetterParams) =>
        decodeLegacyText(params.value as string),
    },
    {
      field: "referencia",
      headerName: "Referência",
      flex: 1,
      minWidth: 120,
      valueGetter: (params: GridValueGetterParams) =>
        decodeLegacyText(params.value as string),
    },
  ];

  const columnsComponentes: GridColDef[] = [
    {
      field: "ordem",
      headerName: "#",
      width: 60,
    },
    {
      field: "urlImagem",
      headerName: "Imagem",
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <ImageCell 
          src={params.value} 
          alt={params.row.codigoComponente || "Imagem do componente"} 
        />
      ),
    },
    {
      field: "codigoComponente",
      headerName: "Código",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {params.value}
          {params.row.descontinuado && (
            <Tooltip title={params.row.motivoDescontinuacao || "Componente descontinuado"}>
              <Warning color="error" fontSize="small" />
            </Tooltip>
          )}
          {params.row.temVersaoAtualizada && (
            <Tooltip title={`Versão atualizada: ${params.row.codigoComponenteAtualizado}`}>
              <Update color="primary" fontSize="small" />
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      field: "descricaoComponente",
      headerName: "Descrição",
      flex: 2,
      minWidth: 250,
      valueGetter: (params: GridValueGetterParams) =>
        decodeLegacyText(params.value as string),
    },
    {
      field: "referencia",
      headerName: "Referência",
      flex: 1,
      minWidth: 120,
      valueGetter: (params: GridValueGetterParams) =>
        decodeLegacyText(params.value as string),
    },
  ];

  if (isLoading && !tipologia) {
    return (
      <DashboardStackCustom>
        <NavigationHeader
          title="CADASTRO DE TIPOLOGIAS"
          label="Carregando..."
          hasBackButton={true}
          goBack={() => navigate("/tipologiasMof")}
        />
        <Paper sx={{ p: 3, textAlign: "center" }}>
          Carregando dados da tipologia...
        </Paper>
      </DashboardStackCustom>
    );
  }

  if (!tipologia) {
    return (
      <DashboardStackCustom>
        <NavigationHeader
          title="CADASTRO DE TIPOLOGIAS"
          label="Erro"
          hasBackButton={true}
          goBack={() => navigate("/tipologiasMof")}
        />
        <Paper sx={{ p: 3, textAlign: "center", color: "error.main" }}>
          Tipologia não encontrada
        </Paper>
      </DashboardStackCustom>
    );
  }

  const descricaoSolicitacao = formatDisplayText(tipologia.descricaoSolicitacao);
  const situacao = formatDisplayText(tipologia.situacao);
  const descricaoTipologia = formatDisplayText(tipologia.descricao || tipologia.descricaoSolicitacao);
  const linhaNome = formatDisplayText(tipologia.linhaNome);
  const grupoNome = formatDisplayText(tipologia.grupoNome);

  const isConsulta = mode === "consulta";

  const navigationTitle = isConsulta ? "TIPOLOGIAS" : "Solicitações > Tipologias";
  const navigationLabel = isConsulta ? "Consulta tipologia" : "Consultar tipologia";
  const navigationLinks = isConsulta
    ? [{ label: "Tipologias", link: "/tipologiasMof" }]
    : [{ label: "Solicitações", link: "/solicitacoesMof" }];
  const navigationBackPath = isConsulta ? "/tipologiasMof" : "/solicitacoesMof";

  return (
    <DashboardStackCustom>
      <NavigationHeader
        title={navigationTitle}
        label={navigationLabel}
        links={navigationLinks}
        hasBackButton={true}
        goBack={() => navigate(navigationBackPath)}
        customElement={
          <Grid
            item
            xs={12}
            sm={6}
            display="flex"
            justifyContent="flex-end"
            alignItems="flex-start"
            sx={{ mt: { xs: 2, sm: 0 } }}
          >
            {/* Informações da Solicitação no topo direito */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 3,
                alignItems: "flex-start",
              }}
            >
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem", display: "block", mb: 0.25 }}>
                  Número da solicitação
                </Typography>
                <Typography variant="body1" fontWeight={700} sx={{ color: "text.primary", lineHeight: 1.2, fontSize: "0.875rem" }}>
                  {tipologia.numeroSolicitacao
                    ? String(tipologia.numeroSolicitacao).padStart(7, "0")
                    : "-"}
                </Typography>
              </Box>

              <Box sx={{ textAlign: "right", minWidth: 200 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem", display: "block", mb: 0.25 }}>
                  Descrição da solicitação
                </Typography>
                <Typography variant="body2" sx={{ color: "text.primary", fontSize: "0.875rem", lineHeight: 1.4 }}>
                  {descricaoSolicitacao}
                </Typography>
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem", display: "block", mb: 0.25 }}>
                    Situação atual
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.primary", fontSize: "0.875rem", lineHeight: 1.4 }}>
                    {situacao}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        }
      />

      {/* Abas - Perfil e Componentes (posicionadas logo após o título) */}
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" spacing={0}>
          <Button
            variant={tabValue === 0 ? "contained" : "outlined"}
            onClick={() => handleButtonTabChange(0)}
            sx={{
              borderRadius: "4px 0 0 4px",
              textTransform: "none",
              fontWeight: 600,
              minWidth: 120,
              minHeight: 58,
              height: 58,
            }}
          >
            PERFIL
          </Button>
          <Button
            variant={tabValue === 1 ? "contained" : "outlined"}
            onClick={() => handleButtonTabChange(1)}
            sx={{
              borderRadius: "0 4px 4px 0",
              textTransform: "none",
              fontWeight: 600,
              minWidth: 120,
              minHeight: 58,
              height: 58,
            }}
          >
            COMPONENTES
          </Button>
        </Stack>
      </Box>

      {/* Detalhes da Tipologia */}
      <Paper elevation={3} sx={{ p: 3, mb: 2 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            {(() => {
              // Priorizar croqui da tipologia (urlDesenhoSvg)
              if (tipologia.urlDesenhoSvg) {
                const croquiUrl = buildImageSrc(tipologia.urlDesenhoSvg);
                if (croquiUrl) {
                  return (
                    <CroquiImageWithFallback 
                      src={croquiUrl}
                      alt={`Croqui ${tipologia.codigo}`}
                    />
                  );
                }
              }

              // Se não tem urlDesenhoSvg, buscar nas imagens carregadas
              // Priorizar imagens do tipo "Croqui" ou "CROQUI" ou "desenho"
              if (imagens && imagens.length > 0) {
                const croquiImagem = imagens.find(img => 
                  img.tipoImagem?.toLowerCase().includes("croqui") || 
                  img.tipoImagem?.toLowerCase().includes("desenho") ||
                  img.tipoImagem?.toLowerCase().includes("croquis")
                ) || imagens[0]; // Fallback para primeira imagem

                if (croquiImagem?.urlImagem) {
                  const imagemUrl = buildImageSrc(croquiImagem.urlImagem);
                  if (imagemUrl) {
                    return (
                      <CroquiImageWithFallback 
                        src={imagemUrl}
                        alt={croquiImagem.titulo || `Croqui ${tipologia.codigo}`}
                      />
                    );
                  }
                }
              }

              // Placeholder se não houver imagem
              return (
                <Box
                  sx={{
                    width: "100%",
                    height: "200px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px dashed #ccc",
                    borderRadius: 1,
                    color: "#999",
                  }}
                >
                  Sem imagem
                </Box>
              );
            })()}
            {tipologia.nomeArquivoDesenho && (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: "block", textAlign: "center" }}>
                {tipologia.nomeArquivoDesenho}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} md={9}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" sx={{ color: "primary.main", fontSize: "0.75rem" }}>
                      Código
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: "0.75rem", mt: 0.5 }}>{tipologia.codigo}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" sx={{ color: "primary.main", fontSize: "0.75rem" }}>
                      Descrição
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: "0.75rem", mt: 0.5 }}>{descricaoTipologia}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" sx={{ color: "primary.main", fontSize: "0.75rem" }}>
                      Linha
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: "0.75rem", mt: 0.5 }}>{linhaNome}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" sx={{ color: "primary.main", fontSize: "0.75rem" }}>
                      Grupo
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: "0.75rem", mt: 0.5 }}>{grupoNome}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" sx={{ color: "primary.main", fontSize: "0.75rem" }}>
                      Situação
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: "0.75rem", mt: 0.5 }}>{situacao}</Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderLeft: { md: "1px solid #e0e0e0" },
                    pl: { md: 3 },
                    pt: { xs: 3, md: 0 },
                  }}
                >
                  {tipologia.projetistaLogoUrl ? (
                    <Box
                      component="img"
                      src={buildImageSrc(tipologia.projetistaLogoUrl)}
                      alt={tipologia.projetistaNome || "Logo do projetista"}
                      sx={{
                        maxWidth: 140,
                        maxHeight: 140,
                        width: "auto",
                        height: "auto",
                        borderRadius: 2,
                        objectFit: "contain",
                      }}
                      onError={(e) => {
                        // Se a imagem falhar ao carregar, mostra placeholder
                        const target = e.target as HTMLImageElement;
                        target.style.display = "none";
                        const placeholder = target.nextElementSibling as HTMLElement;
                        if (placeholder) placeholder.style.display = "flex";
                      }}
                    />
                  ) : null}
                  <Box
                    sx={{
                      width: 140,
                      height: 140,
                      borderRadius: 2,
                      border: "1px dashed #cfd8dc",
                      backgroundColor: "#f5f7fa",
                      display: tipologia.projetistaLogoUrl ? "none" : "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#90a4ae",
                      fontSize: 12,
                      textTransform: "uppercase",
                      letterSpacing: 1,
                    }}
                  >
                    {tipologia.projetistaNome ? tipologia.projetistaNome : "Logo"}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {/* Campo de busca local */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, backgroundColor: "#f8f9fa" }}>
        <TextField
          size="small"
          placeholder="Pesquise por código"
          value={searchLocal}
          onChange={handleSearchLocal}
          sx={{ maxWidth: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Conteúdo das Abas - Perfil, Componentes e Imagens */}
      <Paper elevation={3}>
        <TabPanel value={tabValue} index={0}>
          <StripedDataGrid
            columns={columnsPerfis}
            rows={perfis}
            getRowId={(row) => row.idTipologiaPerfil}
            loading={isLoading}
            autoHeight
            disableColumnMenu
            hideFooter
            checkboxSelection={false}
            getRowClassName={(params) =>
              params.row.descontinuado ? "row-error" : ""
            }
            sx={{
              "& .row-error": {
                backgroundColor: "#ffebee !important",
              },
            }}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <StripedDataGrid
            columns={columnsComponentes}
            rows={componentes}
            getRowId={(row) => row.idTipologiaComponente}
            loading={isLoading}
            autoHeight
            disableColumnMenu
            hideFooter
            checkboxSelection={false}
            getRowClassName={(params) =>
              params.row.descontinuado ? "row-error" : ""
            }
            sx={{
              "& .row-error": {
                backgroundColor: "#ffebee !important",
              },
            }}
          />
        </TabPanel>

      </Paper>
    </DashboardStackCustom>
  );
};

export const TipologiaDetalhe = () => <TipologiaDetalheInner mode="solicitacao" />;

export const ConsultaTipologia = () => <TipologiaDetalheInner mode="consulta" />;
